import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, StatusPill, EmptyState } from "@/components/app/Primitives";
import { ReviewConfirmDialog } from "@/components/app/ReviewConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import TID from "@/lib/testIds";
import { Shield, User as UserIcon, RefreshCcw, LogIn } from "lucide-react";

/* L3 admin drawer — status override, edit contact, impersonate, audit trail. */
function L3AdminDrawer({ influencer, open, onOpenChange, onDone }) {
  const [form, setForm] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const otherStatuses = useMemo(() => ["invited", "pending", "in_review", "verified", "rejected"].filter((s) => s !== form?.compliance_status), [form?.compliance_status]);

  useEffect(() => {
    if (!influencer || !open) return;
    setForm({
      legal_name: influencer.legal_name || "",
      email: influencer.email || "",
      mobile: influencer.mobile || "",
      country: influencer.country || "India",
      compliance_status: influencer.compliance_status || "invited",
    });
    api
      .get(`/audit-logs?entity_id=${influencer.id}`)
      .then((r) => setAudit(r.data.slice(0, 20)))
      .catch(() => setAudit([]));
  }, [influencer, open]);

  if (!influencer || !form) return null;

  const patchInfluencer = async () => {
    setBusy(true);
    try {
      await api.patch(`/influencers/${influencer.id}`, {
        legal_name: form.legal_name,
        email: form.email,
        mobile: form.mobile,
        country: form.country,
      });
      toast.success("Contact updated (audit logged)");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const overrideStatus = async (newStatus) => {
    setBusy(true);
    try {
      if (newStatus === "verified") {
        await api.post(`/kys/approve/${influencer.id}`);
      } else if (newStatus === "rejected") {
        await api.post(`/kys/reject/${influencer.id}`, { reason: "L3 override" });
      } else {
        await api.patch(`/influencers/${influencer.id}`, { compliance_status: newStatus });
      }
      toast.success(`Status → ${newStatus} (audit logged)`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Override failed");
    } finally {
      setBusy(false);
    }
  };

  const impersonate = async () => {
    setBusy(true);
    try {
      // Look up the influencer's user record to get user_id.
      const usersResp = await api.get(`/users?email=${encodeURIComponent(form.email)}`);
      const target = (usersResp.data || []).find((u) => u.role === "influencer");
      if (!target) {
        toast.error("No user account found for this influencer");
        return;
      }
      await api.post(`/admin/impersonate/${target.id}`);
      toast.success(`Now acting as ${target.name}`);
      // Reload to hydrate the new session cookie.
      window.location.href = "/influencer";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="l3-admin-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={18} className="text-[#0052FF]" />
            L3 Admin actions — {influencer.legal_name}
          </DialogTitle>
          <DialogDescription>
            Every action is captured in the audit log with the admin identity. Use only for support / troubleshooting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Contact */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Legal name</label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">Country</label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">Email</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">Mobile</label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            </div>
            <Button size="sm" className="mt-3" onClick={patchInfluencer} disabled={busy} data-testid="l3-save-contact">Save contact</Button>
          </div>

          {/* Status override */}
          <div className="border-t pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Compliance status override</div>
            <div className="flex flex-wrap gap-2 items-center">
              <StatusPill status={form.compliance_status} />
              <span className="text-slate-400 text-sm">→</span>
              {otherStatuses
                .map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant="outline"
                    onClick={() => overrideStatus(s)}
                    disabled={busy}
                    data-testid={`l3-status-${s}`}
                    className="text-xs capitalize"
                  >
                    <RefreshCcw size={12} className="mr-1" /> {s.replace("_", " ")}
                  </Button>
                ))}
            </div>
          </div>

          {/* Impersonate */}
          <div className="border-t pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Act on behalf of</div>
            <p className="text-xs text-slate-500 mb-2">
              Sign in as this influencer to unblock a stuck workflow. Your admin identity is preserved in the audit log.
            </p>
            <ReviewConfirmDialog
              trigger={
                <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="l3-impersonate-btn">
                  <LogIn size={14} className="mr-1.5" /> Impersonate {influencer.legal_name}
                </Button>
              }
              title="Impersonate this influencer?"
              subtitle="You will be signed in as this influencer. All actions you take will be attributed to them, with your admin identity in the audit trail. Sign out to return to your admin account."
              details={{
                "Target user": influencer.legal_name,
                Email: influencer.email,
                Mobile: influencer.mobile,
                "Compliance status": influencer.compliance_status,
              }}
              confirmLabel="Yes, impersonate"
              confirmTone="warning"
              onConfirm={impersonate}
              testIdConfirm="l3-impersonate-confirm"
            />
          </div>

          {/* Audit trail */}
          <div className="border-t pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
              <UserIcon size={12} /> Recent audit trail ({audit.length})
            </div>
            {audit.length === 0 ? (
              <div className="text-xs text-slate-400">No entries.</div>
            ) : (
              <div className="max-h-52 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0"><tr>
                    <th className="text-left px-2 py-1.5">When</th>
                    <th className="text-left px-2 py-1.5">Actor</th>
                    <th className="text-left px-2 py-1.5">Action</th>
                  </tr></thead>
                  <tbody>{audit.map((a) => (
                    <tr key={a.id} className="border-t">
                      <td className="px-2 py-1 text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="px-2 py-1">{a.user_email}<div className="text-[10px] text-slate-400">{a.user_role}</div></td>
                      <td className="px-2 py-1 font-mono">{a.action}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Influencers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/influencers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (i) => { await api.post(`/kys/approve/${i.id}`); toast.success("Approved"); load(); };
  const reject = async (i, comment) => { await api.post(`/kys/reject/${i.id}`, { reason: comment }); toast.success("Rejected"); load(); };

  const isAdmin = user.role === "propay_admin";

  return (
    <div className="space-y-6">
      <Section
        title="Influencer master"
        subtitle={isAdmin
          ? "All influencers · click a row for L3 admin actions (status override, edit contact, impersonate). Every action is audited."
          : "All influencers invited by FMCG customers or self-registered."}
      />
      {items.length === 0 ? (
        <EmptyState title="No influencers yet" />
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Contact</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Country</th>
                <th className="px-4 py-3 text-left">Compliance</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr
                  key={i.id}
                  data-testid={TID.influencerRow(i.id)}
                  onClick={() => isAdmin && setSelected(i)}
                  className={`border-t hover:bg-slate-50 ${isAdmin ? "cursor-pointer" : ""}`}
                >
                  <td className="px-4 py-3 font-semibold">
                    {i.legal_name}
                    {isAdmin && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-[#0052FF]">L3 ↗</span>}
                  </td>
                  <td className="px-4 py-3"><div>{i.email}</div><div className="text-xs text-slate-500">{i.mobile}</div></td>
                  <td className="px-4 py-3 capitalize">{i.influencer_type}</td>
                  <td className="px-4 py-3">{i.country}</td>
                  <td className="px-4 py-3"><StatusPill status={i.compliance_status} /></td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {(user.role === "procurement_desk" || isAdmin) && i.compliance_status === "in_review" && (
                      <>
                        <ReviewConfirmDialog
                          trigger={<Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">Approve</Button>}
                          title="Approve KYS?"
                          subtitle="Once approved the influencer is eligible to receive Purchase Orders from PROPAY GLOBAL PRIVATE LIMITED."
                          details={{
                            "Legal name": i.legal_name,
                            Email: i.email,
                            Mobile: i.mobile,
                            Type: i.influencer_type,
                            Country: i.country,
                            "KYS checks": Object.keys(i.kys_checks || {}).map((k) => `${k}: ${i.kys_checks[k]?.ok ? "✓" : "✗"}`).join(" · ") || "—",
                            "Bank last4": i.bank?.account_last4 ? `••${i.bank.account_last4}` : "—",
                          }}
                          confirmLabel="Approve KYS"
                          confirmTone="success"
                          onConfirm={() => approve(i)}
                          testIdOpen={TID.kysApproveBtn(i.id)}
                          testIdConfirm={`confirm-kys-approve-${i.id}`}
                        />
                        <span className="ml-2 inline-block">
                          <ReviewConfirmDialog
                            trigger={<Button size="sm" variant="destructive">Reject</Button>}
                            title="Reject KYS?"
                            subtitle="The influencer will be notified with the reason and cannot receive POs until they re-submit."
                            details={{
                              "Legal name": i.legal_name,
                              Email: i.email,
                              Mobile: i.mobile,
                            }}
                            confirmLabel="Reject"
                            confirmTone="danger"
                            requireComment commentRequired commentLabel="Reason"
                            onConfirm={(c) => reject(i, c)}
                            testIdConfirm={`confirm-kys-reject-${i.id}`}
                          />
                        </span>
                      </>
                    )}
                    {(user.role === "procurement_desk" || isAdmin) && i.compliance_status === "verified" && (
                      <span className="text-xs text-emerald-700 font-semibold">Ready for PO</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <L3AdminDrawer
        influencer={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        onDone={() => { load(); setSelected(null); }}
      />
    </div>
  );
}
